import React, { createContext, useCallback, useContext, useRef, useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button } from '@mui/material';

export interface ConfirmOptions {
  title: string;
  message: React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  /** 'destructive' red-confirms a one-way action (delete, discard, disconnect);
   *  'primary' is a plain go-ahead. Defaults to 'primary'. */
  variant?: 'destructive' | 'primary';
}

interface PendingConfirm {
  title: string;
  message: React.ReactNode;
  confirmLabel: string;
  cancelLabel: string;
  variant: 'destructive' | 'primary';
}

type ConfirmFn = (options: ConfirmOptions) => Promise<boolean>;

const ConfirmContext = createContext<ConfirmFn | null>(null);

/** App-wide replacement for window.confirm() and the one-off <Dialog> each
 *  page used to hand-roll for its own delete/discard confirmation (see
 *  TrainingTracks.tsx's old "Delete Training Track" dialog). Mount once near
 *  the app root; call sites use useConfirm() and await the boolean result
 *  the same way they'd check window.confirm()'s return value. */
export const ConfirmProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [pending, setPending] = useState<PendingConfirm | null>(null);
  const resolveRef = useRef<((confirmed: boolean) => void) | null>(null);

  const settle = useCallback((confirmed: boolean) => {
    resolveRef.current?.(confirmed);
    resolveRef.current = null;
    setPending(null);
  }, []);

  const confirm = useCallback<ConfirmFn>((options) => (
    new Promise<boolean>((resolve) => {
      resolveRef.current = resolve;
      setPending({
        title: options.title,
        message: options.message,
        confirmLabel: options.confirmLabel || 'Confirm',
        cancelLabel: options.cancelLabel || 'Cancel',
        variant: options.variant || 'primary',
      });
    })
  ), []);

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <Dialog open={!!pending} onClose={() => settle(false)} maxWidth="sm" fullWidth>
        {pending && (
          <>
            <DialogTitle>{pending.title}</DialogTitle>
            <DialogContent>
              <DialogContentText component="div">{pending.message}</DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => settle(false)}>{pending.cancelLabel}</Button>
              <Button
                onClick={() => settle(true)}
                variant="contained"
                color={pending.variant === 'destructive' ? 'error' : 'primary'}
                autoFocus
              >
                {pending.confirmLabel}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </ConfirmContext.Provider>
  );
};

/** Returns a function that shows the shared confirm dialog and resolves to
 *  whether the user confirmed — a drop-in async replacement for
 *  `window.confirm(message)`:
 *
 *    if (!(await confirm({ title, message, variant: 'destructive' }))) return;
 *
 *  Falls back to window.confirm() (with a console warning) when rendered
 *  outside a ConfirmProvider, e.g. a component test that mounts this piece
 *  in isolation — the app itself always provides one from the root, so this
 *  only matters for call sites that genuinely aren't under it. */
export const useConfirm = (): ConfirmFn => {
  const confirm = useContext(ConfirmContext);
  return confirm || ((options) => {
    console.warn('[useConfirm] No ConfirmProvider in the tree — falling back to window.confirm().');
    const text = typeof options.message === 'string' ? `${options.title}\n\n${options.message}` : options.title;
    return Promise.resolve(window.confirm(text));
  });
};
